import { Component, NgZone, inject } from '@angular/core';

import { IonApp, IonRouterOutlet, IonFab, IonFabButton, IonIcon, Platform } from '@ionic/angular/standalone';
import { Router, RouterModule } from '@angular/router';
import { App } from '@capacitor/app';
import { addIcons } from 'ionicons'
import { chevronForwardOutline, flask } from 'ionicons/icons';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { environment } from '@storees/environments';
import { AuthService } from './services/auth-service';
import { StoreesBackendService } from './services/storees-backend.service';
import { SafeArea } from '@capacitor-community/safe-area';
import { NarratorService } from './services/narrator-service';
import {
  Entitlement,
  EntitlementsCacheLifetime,
  Environment,
  LaunchMode,
  Qonversion,
  QonversionConfigBuilder,
  UserPropertyKey,
} from '@qonversion/capacitor-plugin';
import { StoreesSubscriptionService } from './services/storees-subscription-service';
import { DexieProfilesService } from './services/dexie/dexie-profiles.service';
import { DexieTrophiesService } from './services/dexie/dexie-trophies-service';
import { preloadAudio, PRELOADED_AUDIO_ASSET_IDS } from './utils/utils';
import { AudioAssetId, STORY_PLAYER } from '@storees/shared-constants';
import { ProfileService } from './services/profile-service';
import { AlertController } from '@ionic/angular/standalone';
import { FirebaseAnalyticsService } from './services/firebase/FirebaseAnalyticsService';
import { RouteTrackingService } from './services/route-tracking.service';
import { logBillingError } from './utils/billing-error-logger';
import { logger } from './utils/logger';
import { firstValueFrom } from 'rxjs';
import { PushNotificationService } from './services/push-notification.service';
import { InstallReferrerService } from './services/install-referrer.service';
import { CampaignFatigueActivityService } from './services/campaign-fatigue-activity.service';
import { StoryShareImportService } from './services/story-share-import.service';
import { ShareDeepLinkParamsService } from './services/share-deep-link-params.service';
import { AdConsentService } from './services/ad-consent.service';

const { qonversionProjectKey } = environment;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: true,
  imports: [IonApp, IonRouterOutlet, IonFab, IonFabButton, IonIcon, RouterModule],
})
export class AppComponent {
  private platform = inject(Platform);
  private authService = inject(AuthService);
  private narratorService = inject(NarratorService);
  private storeesBackendService = inject(StoreesBackendService);
  private storeesSubscriptionService = inject(StoreesSubscriptionService);
  private profileService = inject(ProfileService);
  private profilesService = inject(DexieProfilesService);
  private dexieTrophiesService = inject(DexieTrophiesService);
  private ngZone = inject(NgZone);
  private alertController = inject(AlertController);
  private firebaseAnalytics = inject(FirebaseAnalyticsService);
  private routeTracking = inject(RouteTrackingService);
  private pushNotificationService = inject(PushNotificationService);
  private router = inject(Router);
  private installReferrerService = inject(InstallReferrerService);
  private campaignFatigueActivityService = inject(CampaignFatigueActivityService);
  private storyShareImportService = inject(StoryShareImportService);
  private shareDeepLinkParams = inject(ShareDeepLinkParamsService);
  private adConsentService = inject(AdConsentService);

  public static audioAssetIds: string[] = [
    AudioAssetId.AF_BELLA_INTRO,
    AudioAssetId.AF_HEART_INTRO,
    AudioAssetId.AF_NICOLE_INTRO,
    AudioAssetId.BF_EMMA_INTRO,
    AudioAssetId.FF_SIWIS_INTRO,
  ];

  // Getter to check if we're in development mode
  get isDevelopment(): boolean {
    return !environment.production;
  }

  /** Keeps streak-pools FAB from covering full-page flows that pin content to the bottom. */
  get showDevTestFab(): boolean {
    return !this.router.url.includes('/voice-clone');
  }

  constructor() {
    // Register icons for development test button + shared selection-arrow chevrons (Image Style, Narrator Voice)
    addIcons({ chevronForwardOutline, flask });

    this.onPlatformReady().then(() => {
      console.log('onPlatformReady() resolved');
    });
  }

  private async onPlatformReady() {
    await this.platform.ready();
    this.registerUniversalLinkHandler();

    // Qonversion.initialize() must not wait on JWT or network — updateSessionJWT() can block on
    // getAnonymousToken() while the router already shows the paywall and calls fetchOfferings().
    this.initializeQonversionSdk();
    await this.syncFirebaseAppInstanceIdToQonversion();

    if (Capacitor.isNativePlatform()) {
      try {
        const safeArea = SafeArea as {
          setSystemBarsStyle?: (o: { style: string }) => Promise<void>;
          enable?: (o?: unknown) => Promise<void>;
        };
        if (typeof safeArea.setSystemBarsStyle === 'function') {
          // LIGHT = dark/black bar content on light background (status + nav on Android; iOS status).
          await safeArea.setSystemBarsStyle({ style: 'LIGHT' });
        } else if (Capacitor.getPlatform() === 'ios' && typeof safeArea.enable === 'function') {
          await safeArea.enable?.({});
        }
      } catch (_) {
        // Ignore if plugin not fully available
      }
    }
    await SplashScreen.hide();
    if (Capacitor.isNativePlatform()) {
      try {
        const safeAreaAgain = SafeArea as {
          setSystemBarsStyle?: (o: { style: string }) => Promise<void>;
        };
        // Splash hide / focus changes can reset on-screen navigation bar appearance (all vendors).
        if (typeof safeAreaAgain.setSystemBarsStyle === 'function') {
          await safeAreaAgain.setSystemBarsStyle({ style: 'LIGHT' });
        }
      } catch (_) {
        // Ignore if plugin not fully available
      }
    }
    if (Capacitor.getPlatform() === 'android') {
      try {
        // Style.Light = dark/black icons (Capacitor naming). Re-apply after splash — hide can reset appearance.
        await StatusBar.setStyle({ style: Style.Light });
      } catch (_) {
        // Ignore if plugin not fully available
      }
    }
    await this.authService.updateSessionJWT();
    const jwtToken = await this.authService.getSessionJwtToken();

    // Start profile sync early (non-blocking) so it can finish during welcome/onboarding.
    if (jwtToken) {
      void this.profilesService
        .syncProfiles(jwtToken, { storyLists: false })
        .catch((error) => {
          console.error('AppComponent - Early profile sync failed:', error);
        });
    }

    try {
      await firstValueFrom(
        this.profilesService.addProfile(jwtToken, ProfileService.ADD_NEW_PROFILE, {
          awaitSync: false,
        })
      );
      this.profileService.refreshProfiles();
    } catch (error) {
      console.warn('Warn - ADD NEW PROFILE likely exists already: ', error);
    }
    await this.profileService.repairSelectedProfileIfInvalid();

    await this.linkQonversionToUserAndSyncEntitlements();

    // Set user ID if available
    const userId = await this.authService.getUserId();
    if (userId) {
      await this.firebaseAnalytics.setUser(userId);
    }

    await this.firebaseAnalytics.logAppOpen();

    // Start automatic route tracking
    await this.routeTracking.startTracking();

    await this.storeesBackendService.appOpen(jwtToken);
    const shareImportFlush =
      await this.storyShareImportService.tryFlushPendingImport();
    logger.warn('[IMPORT_SHARED_STORY] AppComponent after appOpen', {
      tryFlushPendingImport: shareImportFlush.flushed,
      shareImportEntryStoryId: shareImportFlush.entryStoryId,
    });
    await this.pushNotificationService.checkRefreshAndSyncPushToken(jwtToken);
    await this.campaignFatigueActivityService.start();
    await this.narratorService.getNarrators();
    await this.preloadAudio();

    try {
      await this.profilesService.syncProfiles(jwtToken, { storyLists: false });
    } catch (error) {
      console.error('AppComponent - Profile sync failed, continuing with local data:', error);
    }
    await this.profileService.repairSelectedProfileIfInvalid();
    await this.storyShareImportService.tryFlushPendingImport();

    try {
      await this.dexieTrophiesService.syncTrophies(jwtToken);
    } catch (error) {
      console.error('AppComponent - Trophy sync failed, continuing with local data:', error);
    }

    await this.adConsentService.preload();

    await this.installReferrerService.checkAndNavigate();
  }

  /**
   * When the app is opened from a Universal Link (iOS) / App Link (Android), route into story-player.
   */
  private registerUniversalLinkHandler(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const navigateFromStoryPlayerUrl = (url: string): void => {
      if (!this.shareDeepLinkParams.ingestUrl(url)) {
        return;
      }
      const pending = this.shareDeepLinkParams.peek();
      if (!pending) {
        return;
      }
      this.ngZone.run(() => {
        void this.router.navigate([`/${STORY_PLAYER}`], {
          queryParams: {
            storyId: pending.storyId,
            createdAt: pending.createdAt,
          },
          replaceUrl: true,
        });
      });
    };

    void App.getLaunchUrl()
      .then((launch) => {
        if (launch?.url) {
          navigateFromStoryPlayerUrl(launch.url);
        }
      })
      .catch((e) => {
        console.warn('App getLaunchUrl failed', e);
      });

    void App.addListener('appUrlOpen', ({ url }) => {
      try {
        navigateFromStoryPlayerUrl(url);
      } catch (e) {
        console.warn('App URL open could not be handled', e);
      }
    });
  }

  async preloadAudio() {
    for (const audioAssetId of PRELOADED_AUDIO_ASSET_IDS) {
      const audioAssetPath = Capacitor.isNativePlatform()
        ? `public/assets/sounds/${audioAssetId}.mp3`
        : `${audioAssetId}.mp3`;
      await preloadAudio(audioAssetId, audioAssetPath, 1, false);
    }
  }

  /**
   * Sync entry; safe to call before JWT. `configurePurchasesWithAppUserId` runs later in
   * {@link linkQonversionToUserAndSyncEntitlements}.
   */
  private initializeQonversionSdk(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      let configBuilder = new QonversionConfigBuilder(
        qonversionProjectKey,
        LaunchMode.SUBSCRIPTION_MANAGEMENT
      );
      if (environment.stackName === 'staging') {
        configBuilder = configBuilder.setEnvironment(Environment.SANDBOX);
        console.log(
          'Qonversion - Using Sandbox environment for staging (improved Play Billing logging)'
        );
      }
      if (EntitlementsCacheLifetime?.WEEK != null) {
        configBuilder = configBuilder.setEntitlementsCacheLifetime(
          EntitlementsCacheLifetime.WEEK
        );
      }
      const config = configBuilder
        .setEntitlementsUpdateListener({
          onEntitlementsUpdated: (entitlements: Map<string, Entitlement>) => {
            this.storeesSubscriptionService.applyEntitlementsFromQonversion(
              entitlements
            );
          },
        })
        .build();
      Qonversion.initialize(config);
      console.log('Qonversion SDK initialized');
    } catch (error: unknown) {
      logBillingError('Qonversion SDK initialization', error);
    }
  }

  private async linkQonversionToUserAndSyncEntitlements(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await this.authService.configurePurchasesWithAppUserId();
      console.log('Qonversion identify / session sync complete');
      await this.storeesSubscriptionService.checkEntitlementsAfterQonversionIdentify();
    } catch (error: unknown) {
      logBillingError('Qonversion user link / entitlements sync', error);
    }
  }

  private async syncFirebaseAppInstanceIdToQonversion(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const appInstanceId = await this.firebaseAnalytics.getNativeAppInstanceId();
      if (!appInstanceId) {
        return;
      }

      Qonversion.getSharedInstance().setUserProperty(
        UserPropertyKey.FIREBASE_APP_INSTANCE_ID,
        appInstanceId
      );
      logger.log(
        'StoreesFirebase - Qonversion FirebaseAppInstanceId user property set:',
        appInstanceId
      );
    } catch (error: unknown) {
      logBillingError(
        'Qonversion setUserProperty(FIREBASE_APP_INSTANCE_ID)',
        error
      );
    }
  }
}
